import { useStore } from '@nanostores/react';

import { useSearchParams } from '@remix-run/react';
import { memo, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { AcceptInviteScreen } from './Team/AcceptInviteScreen';
import { FreeTeamAdminScreen } from './Team/FreeTeamAdminScreen';
import { FreeTeamMemberScreen } from './Team/FreeTeamMemberScreen';
import { MemberTeamScreen } from './Team/MemberTeamScreen';
import { NoTeamScreen } from './Team/NoTeamScreen';
import { UpdateTeamScreen } from './Team/UpdateTeamScreen/UpdateTeamScreen';

import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { useTeamInvites } from '~/lib/hooks/useTeamInvites';
import { organizationMembersStore } from '~/lib/stores/organizationMembers';
import { pricingStore } from '~/lib/stores/pricing/pricing';
import { teamStore } from '~/lib/stores/team';
import { userStore } from '~/lib/stores/user';
import { AnalyticsAction, AnalyticsTrackEvent, CreateTeamTrigger, PricingPageOpenedTrigger } from '~/types/analytics';
import type { OrganizationInvite } from '~/types/organization';

const displayTeamInviteSuccessToast = (invite: OrganizationInvite) => {
  toast.success(`You have successfully joined the team ${invite?.organization.name}`);
};

interface TeamProps {
  closeDialog: VoidFunction;
}

export const Team = memo(({ closeDialog }: TeamProps) => {
  const updateTeamData = useStore(teamStore.updateTeamData);
  const user = useStore(userStore.user);
  const orgMembers = useStore(organizationMembersStore.activeOrgMembers);
  const activeOrganization = useStore(userStore.getActiveOrganization());
  const [searchParams] = useSearchParams();
  const queryParamUsernameRef = useRef<string | null>(null);

  const { isAccepting, isDenying, accept, deny, invite } = useTeamInvites({
    onAccept: displayTeamInviteSuccessToast,
  });

  const showPricingOverlay = () => {
    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.CreateTeamMenuClicked,
        properties: {
          from: CreateTeamTrigger.SettingsTeamTab,
        },
      },
    });
    closeDialog();
    pricingStore.openPricingOverlay(PricingPageOpenedTrigger.SettingsTeamTab);
  };

  useEffect(() => {
    if (!user?.featureFlags.teamManagement) {
      return;
    }

    const memberUsername = searchParams.get('upgradeTeamMember');

    if (!memberUsername) {
      teamStore.updateTeamData.set(null);
      queryParamUsernameRef.current = null;

      return;
    }

    if (queryParamUsernameRef.current === memberUsername) {
      return;
    }

    if (activeOrganization) {
      const memberToUpdate = orgMembers.find((member) => member.user.username === memberUsername);

      if (memberToUpdate) {
        teamStore.updateTeamData.set({
          organization: activeOrganization,
          type: 'upgrade',
          members: [memberToUpdate],
        });
        queryParamUsernameRef.current = memberUsername;
      }
    }
  }, [searchParams, activeOrganization]);

  if (updateTeamData) {
    return <UpdateTeamScreen showPricingOverlay={showPricingOverlay} />;
  }

  if (!!invite) {
    return (
      <AcceptInviteScreen
        invite={invite}
        onAccept={accept}
        onDeny={deny}
        isAccepting={isAccepting}
        isDenying={isDenying}
      />
    );
  } else if (!activeOrganization) {
    return <NoTeamScreen />;
  }

  if (
    user?.featureFlags.teamManagement &&
    activeOrganization &&
    !activeOrganization.billing &&
    activeOrganization?.role === 'admin'
  ) {
    return <FreeTeamAdminScreen organization={activeOrganization} />;
  }

  if (activeOrganization && !activeOrganization.billing && activeOrganization?.role !== 'admin') {
    return <FreeTeamMemberScreen teamName={activeOrganization.name} />;
  }

  if (activeOrganization.role) {
    return (
      <div>
        <MemberTeamScreen activeOrganization={activeOrganization} showPricingOverlay={showPricingOverlay} />
      </div>
    );
  }

  return null;
});
